import { normalizeRegionCode, normalizeRegionList } from '../Config/franceRegions';
const GEOHASH_BASE32 = '0123456789bcdefghjkmnpqrstuvwxyz';

export const geohashForLocation = ([latitude, longitude], precision = 10) => {
  let latRange = [-90, 90];
  let lngRange = [-180, 180];
  let isEven = true;
  let bit = 0;
  let value = 0;
  let hash = '';

  while (hash.length < precision) {
    const range = isEven ? lngRange : latRange;
    const coordinate = isEven ? longitude : latitude;
    const midpoint = (range[0] + range[1]) / 2;
    if (coordinate >= midpoint) {
      value = (value << 1) + 1;
      range[0] = midpoint;
    } else {
      value <<= 1;
      range[1] = midpoint;
    }
    isEven = !isEven;
    bit += 1;
    if (bit === 5) {
      hash += GEOHASH_BASE32[value];
      bit = 0;
      value = 0;
    }
  }
  return hash;
};

const haversineDistanceKm = ([lat1, lng1], [lat2, lng2]) => {
  const toRadians = (degrees) => degrees * Math.PI / 180;
  const earthRadiusKm = 6371.0088;
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) ** 2;
  return earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const DEFAULT_FRANCE_CENTER = { latitude: 46.603354, longitude: 1.888334 };
export const DEFAULT_SERVICE_RADIUS_KM = 30;
export const ARRIVAL_THRESHOLD_METERS = 300;

export const toCoordinate = (value) => {
  if (!value) return null;
  const latitude = Number(
    value.latitude ?? value.lat ?? value._lat ?? value.location?.latitude ?? value.location?.lat,
  );
  const longitude = Number(
    value.longitude ?? value.lng ?? value.lon ?? value._long ?? value.location?.longitude ?? value.location?.lng,
  );
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null;
  return { latitude, longitude };
};

export const makeGeoLocation = ({
  latitude,
  longitude,
  formattedAddress = '',
  city = '',
  postalCode = '',
  placeId = '',
  department = '',
  region = '',
} = {}) => {
  const coordinate = toCoordinate({ latitude, longitude });
  if (!coordinate) return null;
  return {
    ...coordinate,
    geohash: geohashForLocation([coordinate.latitude, coordinate.longitude]),
    formattedAddress: String(formattedAddress || '').trim(),
    city: String(city || '').trim(),
    postalCode: String(postalCode || '').trim(),
    placeId: String(placeId || '').trim(),
    department: String(department || '').trim(),
    region: String(region || '').trim(),
  };
};

export const approximatePublicLocation = (location) => {
  const normalized = toCoordinate(location);
  if (!normalized) return null;
  // Roughly 1 km precision in metropolitan France: useful for matching without
  // revealing a client's exact doorway before engagement.
  const latitude = Number(normalized.latitude.toFixed(2));
  const longitude = Number(normalized.longitude.toFixed(2));
  const city = String(location.city || '').trim();
  const postalCode = String(location.postalCode || '').trim();
  const label = [postalCode, city].filter(Boolean).join(' ') || 'Localisation approximative';
  return {
    latitude,
    longitude,
    geohash: geohashForLocation([latitude, longitude]),
    city,
    postal_code: postalCode,
    label,
    precision: 'approximate',
  };
};

export const distanceKm = (from, to) => {
  const a = toCoordinate(from);
  const b = toCoordinate(to);
  if (!a || !b) return null;
  return haversineDistanceKm([a.latitude, a.longitude], [b.latitude, b.longitude]);
};

export const formatDistance = (distance) => {
  if (!Number.isFinite(distance)) return 'Distance indisponible';
  if (distance < 1) return `${Math.max(50, Math.round(distance * 1000 / 50) * 50)} m`;
  return `${distance < 10 ? distance.toFixed(1) : Math.round(distance)} km`;
};

export const approximateDriveMinutes = (distance) => {
  if (!Number.isFinite(distance)) return null;
  const estimatedRoadKm = Math.max(1, distance * 1.28);
  return Math.max(4, Math.round((estimatedRoadKm / 45) * 60));
};

export const getDemandPublicLocation = (demand) => (
  toCoordinate(demand?.public_location)
  || toCoordinate(demand?.location)
  || toCoordinate(demand?.exact_location)
);

export const getArtisanServiceLocation = (profile) => (
  toCoordinate(profile?.service_area?.center)
  || toCoordinate(profile?.service_location)
);

export const computeMatchScore = ({ demand, artisanProfile, distance }) => {
  let score = 0;
  if (demand?.demand_type && demand.demand_type === artisanProfile?.speciality) score += 40;

  const radius = Number(artisanProfile?.service_area?.radius_km) || DEFAULT_SERVICE_RADIUS_KM;
  if (Number.isFinite(distance)) {
    score += Math.max(0, 40 * (1 - Math.min(distance, radius) / Math.max(radius, 1)));
  } else {
    const demandRegion = normalizeRegionCode(demand?.demand_region);
    const primaryRegion = normalizeRegionCode(artisanProfile?.artisan_region);
    const serviceRegions = normalizeRegionList(artisanProfile?.service_regions || [primaryRegion]);
    if (artisanProfile?.nationwide_service) score += 18;
    else if (serviceRegions.includes(demandRegion)) score += 18;
    else if (artisanProfile?.accepts_outside_regions) score += 8;
  }

  const imageCount = Array.isArray(demand?.demand_image) ? demand.demand_image.length : 0;
  if (imageCount > 0) score += Math.min(8, imageCount * 2);
  if (String(demand?.description || demand?.demand || '').trim().length >= 80) score += 7;

  const created = demand?.date?.toMillis?.()
    || (demand?.date?.seconds ? demand.date.seconds * 1000 : new Date(demand?.date || 0).getTime());
  if (Number.isFinite(created) && created > 0) {
    const ageDays = (Date.now() - created) / 86400000;
    score += Math.max(0, 5 - ageDays / 3);
  }
  return Math.max(0, Math.min(100, Math.round(score)));
};

export const parseGoogleAddressComponents = (components = []) => {
  const read = (type, short = false) => {
    const component = components.find((item) => item.types?.includes(type));
    return component ? (short ? component.shortText || component.short_name : component.longText || component.long_name) : '';
  };
  return {
    city: read('locality') || read('postal_town') || read('administrative_area_level_2'),
    postalCode: read('postal_code'),
    department: read('administrative_area_level_2'),
    region: read('administrative_area_level_1'),
    countryCode: read('country', true),
  };
};

export const getCurrentPosition = (options = {}) => new Promise((resolve, reject) => {
  if (!navigator.geolocation) {
    reject(new Error('La géolocalisation n’est pas disponible dans ce navigateur.'));
    return;
  }
  navigator.geolocation.getCurrentPosition(
    (position) => resolve({
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
    }),
    (error) => {
      const messages = {
        1: 'Autorisation de localisation refusée. Vous pouvez saisir une adresse manuellement.',
        2: 'Votre position est actuellement indisponible.',
        3: 'La recherche de position a expiré. Réessayez.',
      };
      reject(new Error(messages[error.code] || 'Impossible de récupérer votre position.'));
    },
    { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000, ...options },
  );
});
